import React, { useMemo, useEffect, useRef, useState, useCallback } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import { getSelectedBp } from 'features/bpDetails/ducks/selectors'
import { getCurrentUser, getUserPhotoUrl } from 'features/auth/ducks/selectors'
import {
  selectGuruChatMessages,
  selectGuruChatIsLoading,
  selectGuruChatToolStatus,
  selectGuruChatStreamingText,
  selectGuruChatError,
} from 'ducks/guruChat/selectors'
import {
  loadHistoryRoutine,
  sendMessageRoutine,
  confirmActionRoutine,
  clearChatRoutine,
} from 'ducks/guruChat/actions'
import { selectGuruWidgetPrefilledInput } from 'ducks/guruWidget/selectors'
import { setPrefilledInput } from 'ducks/guruWidget/actions'
import * as guruChatService from 'services/GuruChatService'
import GuruMessageList from './GuruMessageList'
import GuruInput from './GuruInput'
import GuruInsightModal from '../files/GuruInsightModal'
import { IconButton } from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'
import LogoShort from 'assets/images/bp-logo-orange.png'

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: #fff;
`

const ChatHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 20px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.lightGrey};
  background: #fff;
`

const HeaderAvatar = styled.img`
  /* Lock dimensions hard — the source PNG is 2000×2000, so without an
     explicit cap a parent flex stretch or a missing object-fit lets it
     render at intrinsic size. min/max + !important defends against any
     ambient img rules. */
  width: 32px !important;
  height: 32px !important;
  min-width: 32px;
  min-height: 32px;
  max-width: 32px;
  max-height: 32px;
  object-fit: contain;
  flex-shrink: 0;
`

const HeaderTitle = styled.div`
  flex: 1;

  h2 {
    font-size: 18px;
    font-weight: 600;
    margin: 0;
    color: ${({ theme }) => theme.colors.haiti};
  }
`

const HeaderSubtitle = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.grey[200]};
`

const SuggestedQuestions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 0 20px 16px;

  @media (max-width: 768px) {
    display: none;
  }
`

const SuggestedQuestionsTitle = styled.div`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.haiti};
  font-weight: 500;
  margin-bottom: 8px;
  padding: 0 20px;
`

const SuggestedChip = styled.button`
  background: #fff;
  border: 1px solid ${({ theme }) => theme.colors.lightGrey};
  border-radius: 18px;
  padding: 8px 16px;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.haiti};
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: ${({ theme }) => theme.colors.primary}15;
    border-color: ${({ theme }) => theme.colors.primary};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

const SUGGESTED_QUESTIONS_BP = [
  'Give me a building overview',
  'What tasks are overdue?',
  'Show me open critical actions',
  'Log an incident: small leak in the basement plant room',
  'How compliant is this building?',
  'Which certificates expire this year?',
]

const SUGGESTED_QUESTIONS_PORTFOLIO = [
  'Give me a portfolio summary',
  'Which Building Passports have overdue documents?',
  'Show me critical open actions across the portfolio',
  'Which Building Passports have unresolved incidents?',
  'Compare compliance across all Building Passports',
  'Which certificates expire in the next 90 days?',
]

const SUGGESTED_QUESTIONS_ESTATE = [
  'Give me an estate summary',
  'Which Building Passports have overdue documents?',
  'Show me critical open remedials across the estate',
  'Which Building Passports have unresolved incidents?',
  'Compare compliance across all Building Passports in this estate',
  'Which certificates expire in the next 90 days?',
]

const GuruChat = ({ bpId, portfolioId, portfolioName, estateId, estateName, buildingCount, buildings, userMode = false, mode = 'tab' }) => {
  const isWidget = mode === 'widget'
  const dispatch = useDispatch()
  const bp = useSelector(getSelectedBp)
  const currentUser = useSelector(getCurrentUser)
  const userPhoto = useSelector(getUserPhotoUrl)
  const messages = useSelector(selectGuruChatMessages)
  const isLoading = useSelector(selectGuruChatIsLoading)
  const toolStatus = useSelector(selectGuruChatToolStatus)
  const streamingText = useSelector(selectGuruChatStreamingText)
  const error = useSelector(selectGuruChatError)

  const isPortfolioMode = !!portfolioId
  const isUserMode = !!userMode
  const isEstateMode = !!estateId
  // contextId is what we pass to sendMessage/loadHistory. In user mode there
  // is no UUID — the backend keys the conversation off the authenticated user.
  // We still need *something* truthy so the load/send effects below run; the
  // current user's id is the natural choice.
  const contextId = isUserMode
    ? (currentUser?.id || 'user')
    : (isPortfolioMode ? portfolioId : (isEstateMode ? estateId : (bpId || bp?.id)))
  const contextName = isUserMode
    ? 'across your Building Passports'
    : (isPortfolioMode ? portfolioName : (isEstateMode ? estateName : (bpId && bp?.id !== bpId ? null : bp?.name)))

  const bpNameById = useMemo(() => {
    if (!isEstateMode) return null
    const map = {}
    ;(buildings || []).forEach((b) => {
      if (b?.id) map[b.id] = b.name
    })
    return map
  }, [isEstateMode, buildings])

  // Guru Insight modal state. In portfolio mode the citation may belong to
  // any BP in the portfolio, so the message-side passes back the owning BP
  // id with each open. BP chats fall back to the selected bp.
  const [insightFileId, setInsightFileId] = useState(null)
  const [insightBpId, setInsightBpId] = useState(null)
  const [insightOpen, setInsightOpen] = useState(false)
  const handleOpenInsight = useCallback((fileId, bpIdFromCitation) => {
    setInsightFileId(fileId)
    setInsightBpId(bpIdFromCitation || bp?.id || null)
    setInsightOpen(true)
  }, [bp?.id])

  const prefilledInput = useSelector(selectGuruWidgetPrefilledInput)
  const prefilledInputRef = useRef(prefilledInput)
  useEffect(() => { prefilledInputRef.current = prefilledInput }, [prefilledInput])

  // Load conversation history from backend on mount or context change. Skip
  // when the widget is mid-handoff with a pending prefilled question, since
  // loadHistoryRoutine.TRIGGER wipes chat state and would clobber the in-flight
  // send. The check reads from a ref so clearing prefilledInput after sending
  // doesn't re-trigger a history load on the same context.
  useEffect(() => {
    if (!contextId) return
    if (isWidget && prefilledInputRef.current) return
    if (isUserMode) {
      dispatch(loadHistoryRoutine({ userMode: true }))
    } else {
      dispatch(loadHistoryRoutine(isPortfolioMode ? { portfolioId: contextId } : (isEstateMode ? { estateId: contextId } : { bpId: contextId })))
    }
  }, [contextId, isPortfolioMode, isEstateMode, isUserMode, dispatch, isWidget])

  // Auto-send a question passed via ?ask=... (used by the Wiki "Ask Guru" chips).
  const location = useLocation()
  const navigate = useNavigate()
  const autoSentRef = useRef(null)
  useEffect(() => {
    if (!contextId) return
    const params = new URLSearchParams(location.search)
    const ask = params.get('ask')
    if (!ask) return
    const key = `${contextId}:${ask}`
    if (autoSentRef.current === key) return
    autoSentRef.current = key
    dispatch(sendMessageRoutine(
      isUserMode
        ? { userMode: true, question: ask }
        : (isPortfolioMode ? { portfolioId: contextId, question: ask } : (isEstateMode ? { estateId: contextId, question: ask } : { bpId: contextId, question: ask })),
    ))
    params.delete('ask')
    const remaining = params.toString()
    navigate(`${location.pathname}${remaining ? `?${remaining}` : ''}`, { replace: true })
  }, [contextId, isPortfolioMode, isEstateMode, dispatch, location.search, location.pathname, navigate])

  const isRateLimited = error?.errorType === 'rate_limit'
  const hasMessages = messages.length > 0

  const autoSentPrefillRef = useRef(null)
  useEffect(() => {
    if (!isWidget || !contextId || !prefilledInput) return
    const key = `${contextId}:${prefilledInput}`
    if (autoSentPrefillRef.current === key) return
    autoSentPrefillRef.current = key
    dispatch(sendMessageRoutine(
      isUserMode
        ? { userMode: true, question: prefilledInput }
        : (isPortfolioMode ? { portfolioId: contextId, question: prefilledInput } : (isEstateMode ? { estateId: contextId, question: prefilledInput } : { bpId: contextId, question: prefilledInput })),
    ))
    dispatch(setPrefilledInput(''))
  }, [isWidget, contextId, isPortfolioMode, isEstateMode, prefilledInput, dispatch])

  const safeBuildingCount = Number.isFinite(buildingCount) ? buildingCount : null
  const portfolioLabel = contextName
    ? `your portfolio **${contextName}**`
    : 'this portfolio'
  const portfolioCountSuffix = safeBuildingCount !== null
    ? ` (${safeBuildingCount} Building Passport${safeBuildingCount !== 1 ? 's' : ''})`
    : ''
  const estateLabel = contextName
    ? `your estate **${contextName}**`
    : 'this estate'
  const estateCountSuffix = safeBuildingCount !== null
    ? ` (${safeBuildingCount} Building Passport${safeBuildingCount !== 1 ? 's' : ''})`
    : ''

  const welcomeMessage = useMemo(() => ({
    role: 'assistant',
    content: isUserMode
      ? `Hello ${currentUser?.firstName || 'there'}! Welcome to **Guru**: global mode, across every Building Passport you can access.

I can help you with:

- **Queries across Building Passports**: "which Building Passports have overdue tasks?", "list every overdue certificate"
- **Quick search across all your docs**: find a certificate or report by name across every Building Passport
- **Workload review**: see tasks assigned to you everywhere in one go
- **Compliance comparison**: spot which Building Passports are lagging on a particular requirement

Mention a specific Building Passport by name to scope a question to it. Otherwise I'll iterate across all of them.

What would you like to know?`
      : isPortfolioMode
      ? `Hello ${currentUser?.firstName || 'there'}! Welcome to **Guru** for ${portfolioLabel}${portfolioCountSuffix}.

I can help you with:

- **Compliance comparison**: compare status across all Building Passports
- **Document search**: find certificates and reports across the portfolio
- **Expiry tracking**: which Building Passports have overdue or expiring documents
- **Tasks**: view, create, and manage tasks
- **Remedials & incidents**: review open follow-ups, log new incidents, convert remedials into tasks
- **Building Passport details**: ask about any specific Building Passport

What would you like to know?`
      : isEstateMode
      ? `Hello ${currentUser?.firstName || 'there'}! Welcome to **Guru** for ${estateLabel}${estateCountSuffix}.

Ask about compliance, documents, tasks and remedials across this estate's buildings, and its estate-level documents.

I can help you with:

- **Compliance comparison**: compare status across every Building Passport in this estate
- **Document search**: find certificates and reports across the estate, including estate-level documents
- **Expiry tracking**: which Building Passports have overdue or expiring documents
- **Tasks**: view, create, and manage tasks
- **Remedials & incidents**: review open follow-ups, log new incidents, convert remedials into tasks
- **Building Passport details**: ask about any specific Building Passport in this estate

What would you like to know?`
      : `Hello ${currentUser?.firstName || 'there'}! Welcome to **Guru**, your Building Passport assistant for **${contextName || 'this Building Passport'}**.

How can I help you today? I can assist you with:

- **Building overview**: units, deadlines, key contacts, and statistics
- **Tasks**: view, create, update, or assign tasks to team members
- **Remedials**: list open follow-ups, mark them as complete, or convert any remedial into a scheduled task
- **Incidents**: log a fire, leak, fault, near-miss or complaint with suggested follow-up remedials
- **Files & documents**: find certificates, reports, and expiring documents
- **Checklists**: compliance status, missing documents, and checklist progress
- **Members**: look up Admins, Users, residents, and their roles
- **Notes & emails**: search through building notes and inbox emails

Guru remembers the last 30 minutes of conversation. For longer topics, start a new question with full context.

What would you like to know?`,
    timestamp: new Date().toISOString(),
    isWelcome: true,
  }), [currentUser?.firstName, contextName, isPortfolioMode, isEstateMode, isUserMode, buildingCount, portfolioLabel, portfolioCountSuffix, estateLabel, estateCountSuffix])

  const displayMessages = useMemo(() => {
    return hasMessages ? messages : [welcomeMessage]
  }, [messages, hasMessages, welcomeMessage])

  const handleSend = (question) => {
    dispatch(sendMessageRoutine(
      isUserMode
        ? { userMode: true, question }
        : (isPortfolioMode ? { portfolioId: contextId, question } : (isEstateMode ? { estateId: contextId, question } : { bpId: contextId, question })),
    ))
  }

  const handleConfirmAction = async (token, edits, action) => {
    const scopeType = action?.scopeType
    let result
    if (scopeType === 'user') {
      result = await guruChatService.confirmUserAction({ token, edits })
    } else if (scopeType === 'portfolio' || isPortfolioMode) {
      result = await guruChatService.confirmPortfolioAction({ portfolioId: action?.portfolioId || contextId, token, edits })
    } else if (scopeType === 'estate' || isEstateMode) {
      result = await guruChatService.confirmEstateAction({ estateId: action?.estateId || contextId, token, edits })
    } else if (isUserMode) {
      result = await guruChatService.confirmUserAction({ token, edits })
    } else {
      result = await guruChatService.confirmAction({ bpId: contextId, token, edits })
    }
    dispatch(confirmActionRoutine.success(result.data))
    return result
  }

  const handleCancelAction = () => {}

  const handleClear = () => {
    dispatch(clearChatRoutine())
  }

  return (
    <>
    <Container>
      {!isWidget && (
        <ChatHeader>
          <HeaderAvatar src={LogoShort} alt="Guru" />
          <HeaderTitle>
            <h2>Building Passport Guru</h2>
            <HeaderSubtitle>{isPortfolioMode ? `Portfolio: ${contextName} · ${buildingCount} Building Passport${buildingCount !== 1 ? 's' : ''}` : isEstateMode ? `Estate: ${contextName} · ${buildingCount} Building Passport${buildingCount !== 1 ? 's' : ''}` : 'Your Building Passport Assistant'}</HeaderSubtitle>
          </HeaderTitle>
          <IconButton size="small" onClick={handleClear} title="Clear chat">
            <DeleteIcon fontSize="small" />
          </IconButton>
        </ChatHeader>
      )}
      <GuruMessageList
        messages={displayMessages}
        isLoading={isLoading}
        toolStatus={toolStatus}
        streamingText={streamingText}
        userPhoto={userPhoto}
        onConfirmAction={handleConfirmAction}
        onCancelAction={handleCancelAction}
        onOpenInsight={handleOpenInsight}
        portfolioId={isPortfolioMode ? portfolioId : null}
        bpNameById={bpNameById}
      />
      {!hasMessages && !isWidget && (
        <>
          <SuggestedQuestionsTitle>Try asking about:</SuggestedQuestionsTitle>
          <SuggestedQuestions>
            {(isPortfolioMode ? SUGGESTED_QUESTIONS_PORTFOLIO : isEstateMode ? SUGGESTED_QUESTIONS_ESTATE : SUGGESTED_QUESTIONS_BP).map((q, i) => (
              <SuggestedChip
                key={q}
                data-tour-id={i === 0 ? 'guru.suggested' : undefined}
                onClick={() => handleSend(q)}
                disabled={isLoading}
              >
                {q}
              </SuggestedChip>
            ))}
          </SuggestedQuestions>
        </>
      )}
      <GuruInput
        onSend={handleSend}
        disabled={isLoading || isRateLimited}
        placeholder={isRateLimited ? error.message : undefined}
        autoFocus={isWidget}
      />
    </Container>
    {insightOpen && insightFileId && insightBpId && (
      <GuruInsightModal
        open={insightOpen}
        onClose={() => { setInsightOpen(false); setInsightFileId(null); setInsightBpId(null) }}
        file={{ id: insightFileId }}
        bpId={insightBpId}
      />
    )}
    </>
  )
}

export default GuruChat
